/**
 * CompanyFormSettingsService - Manages company-level form configuration
 *
 * This service handles multi-tenant form settings, allowing each company
 * to configure which forms are automatically assigned to new clients.
 *
 * Key Features:
 * - Company-specific form requirements
 * - Auto-assignment configuration
 * - Appointment booking rules
 * - Optional vs required form designation
 *
 * Storage: Firestore collection `company_form_settings` (doc ID = companyId)
 */

import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';

export interface CompanyFormConfiguration {
  companyId: string;
  companyName: string;

  // Forms automatically assigned when client signs up
  autoAssignOnSignup: string[]; // Form template IDs

  // Block appointment booking until these forms are complete
  blockBookingUntilComplete: boolean;

  // Optional forms (recommended but not required for booking)
  optionalForms: string[];

  // Configuration metadata
  updatedAt: string;
  updatedBy: string; // Provider/admin ID who made changes
  version: number;
}

export interface FormRequirementRule {
  formTemplateId: string;
  formName: string;
  category: string;
  isRequired: boolean;
  isAutoAssigned: boolean;
  displayOrder: number;
}

class CompanyFormSettingsService {
  private static instance: CompanyFormSettingsService;
  private listeners: (() => void)[] = [];
  private unsubscribeSnapshot: Unsubscribe | null = null;

  private readonly COLLECTION = 'companyFormSettings';
  private readonly DEFAULT_COMPANY_ID = 'default-company'; // For single-tenant setup

  // In-memory cache for fast synchronous reads after initial load
  private cache: Map<string, CompanyFormConfiguration> = new Map();
  private initialized = false;

  constructor() {
    this.startRealtimeListener();
    // Migrate any leftover localStorage data on first load
    this.migrateFromLocalStorage();
  }

  public static getInstance(): CompanyFormSettingsService {
    if (!CompanyFormSettingsService.instance) {
      CompanyFormSettingsService.instance = new CompanyFormSettingsService();
    }
    return CompanyFormSettingsService.instance;
  }

  /**
   * One-time migration from localStorage to Firestore.
   * Reads old key, writes each entry to Firestore, then removes the key.
   */
  private async migrateFromLocalStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem('company_form_settings');
      if (!stored) return;

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) return;

      for (const config of parsed as CompanyFormConfiguration[]) {
        const ref = doc(db, this.COLLECTION, config.companyId);
        const existing = await getDoc(ref);
        if (!existing.exists()) {
          await setDoc(ref, config);
        }
      }

      localStorage.removeItem('company_form_settings');
      console.info('CompanyFormSettingsService: migrated localStorage data to Firestore');
    } catch (error) {
      console.warn('CompanyFormSettingsService: localStorage migration skipped', error);
    }
  }

  /**
   * Start a Firestore real-time listener on the entire collection.
   * Updates the in-memory cache and notifies subscribers on every change.
   */
  private startRealtimeListener(): void {
    const colRef = collection(db, this.COLLECTION);
    this.unsubscribeSnapshot = onSnapshot(
      colRef,
      (snapshot) => {
        this.cache.clear();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as CompanyFormConfiguration;
          this.cache.set(data.companyId, data);
        });

        // If collection is empty on first load, seed defaults
        if (!this.initialized && this.cache.size === 0) {
          this.initializeDefaultSettings();
        }
        this.initialized = true;
        this.notifyListeners();
      },
      (error) => {
        console.warn('CompanyFormSettingsService: snapshot error (may be expected for client users)', error);
        // If listener fails (e.g., permission denied for client users),
        // seed defaults in cache so the UI still works
        if (!this.initialized && this.cache.size === 0) {
          this.cache.set(this.DEFAULT_COMPANY_ID, {
            companyId: this.DEFAULT_COMPANY_ID,
            companyName: 'MyClinicCore Practice',
            autoAssignOnSignup: ['standard-intake', 'financial-responsibility', 'gad7', 'phq9'],
            blockBookingUntilComplete: true,
            optionalForms: ['covid-screening', 'release-information'],
            updatedAt: new Date().toISOString(),
            updatedBy: 'system',
            version: 1
          });
        }
        this.initialized = true;
        this.notifyListeners();
      }
    );
  }

  private async initializeDefaultSettings(): Promise<void> {
    const defaultSettings: CompanyFormConfiguration = {
      companyId: this.DEFAULT_COMPANY_ID,
      companyName: 'MyClinicCore Practice',
      autoAssignOnSignup: [
        'standard-intake',
        'financial-responsibility',
        'gad7',
        'phq9'
      ],
      blockBookingUntilComplete: true,
      optionalForms: [
        'covid-screening',
        'release-information'
      ],
      updatedAt: new Date().toISOString(),
      updatedBy: 'system',
      version: 1
    };

    try {
      const ref = doc(db, this.COLLECTION, this.DEFAULT_COMPANY_ID);
      await setDoc(ref, defaultSettings);
      // Cache will be updated by the snapshot listener
    } catch (error) {
      console.warn('CompanyFormSettingsService: could not write defaults (expected for non-admin users)', error);
      // Put in cache so the UI still works
      this.cache.set(this.DEFAULT_COMPANY_ID, defaultSettings);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  public subscribe(listener: () => void): void {
    this.listeners.push(listener);
  }

  public unsubscribe(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Get form configuration for a specific company (from cache).
   */
  public getCompanySettings(companyId: string = this.DEFAULT_COMPANY_ID): CompanyFormConfiguration | null {
    return this.cache.get(companyId) || null;
  }

  /**
   * Fetch settings from Firestore directly (async, bypasses cache).
   */
  public async fetchCompanySettings(companyId: string = this.DEFAULT_COMPANY_ID): Promise<CompanyFormConfiguration | null> {
    try {
      const ref = doc(db, this.COLLECTION, companyId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        return snap.data() as CompanyFormConfiguration;
      }
      return null;
    } catch (error) {
      console.error('Error fetching company settings:', error);
      return null;
    }
  }

  /**
   * Get all company settings (from cache).
   */
  public getAllCompanySettings(): CompanyFormConfiguration[] {
    return Array.from(this.cache.values());
  }

  /**
   * Update company form settings (writes to Firestore).
   * Cache is updated automatically via the snapshot listener.
   */
  public async updateCompanySettings(
    companyId: string,
    updates: Partial<CompanyFormConfiguration>,
    updatedBy: string
  ): Promise<CompanyFormConfiguration> {
    const existing = this.cache.get(companyId);
    const ref = doc(db, this.COLLECTION, companyId);

    if (existing) {
      const updated: CompanyFormConfiguration = {
        ...existing,
        ...updates,
        companyId,
        updatedAt: new Date().toISOString(),
        updatedBy,
        version: existing.version + 1
      };
      await setDoc(ref, updated);
      return updated;
    } else {
      const newSettings: CompanyFormConfiguration = {
        companyId,
        companyName: updates.companyName || 'New Company',
        autoAssignOnSignup: updates.autoAssignOnSignup || [],
        blockBookingUntilComplete: updates.blockBookingUntilComplete ?? true,
        optionalForms: updates.optionalForms || [],
        updatedAt: new Date().toISOString(),
        updatedBy,
        version: 1
      };
      await setDoc(ref, newSettings);
      return newSettings;
    }
  }

  /**
   * Get forms that should be auto-assigned to new clients (from cache).
   */
  public getAutoAssignForms(companyId: string = this.DEFAULT_COMPANY_ID): string[] {
    const settings = this.getCompanySettings(companyId);
    return settings?.autoAssignOnSignup || [];
  }

  /**
   * Get optional (recommended) forms (from cache).
   */
  public getOptionalForms(companyId: string = this.DEFAULT_COMPANY_ID): string[] {
    const settings = this.getCompanySettings(companyId);
    return settings?.optionalForms || [];
  }

  /**
   * Check if company blocks appointment booking until forms complete (from cache).
   */
  public shouldBlockBooking(companyId: string = this.DEFAULT_COMPANY_ID): boolean {
    const settings = this.getCompanySettings(companyId);
    return settings?.blockBookingUntilComplete ?? true;
  }

  /**
   * Add a form to auto-assign list.
   */
  public async addAutoAssignForm(
    companyId: string,
    formTemplateId: string,
    updatedBy: string
  ): Promise<void> {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return;

    const currentForms = new Set(settings.autoAssignOnSignup);
    currentForms.add(formTemplateId);

    await this.updateCompanySettings(
      companyId,
      { autoAssignOnSignup: Array.from(currentForms) },
      updatedBy
    );
  }

  /**
   * Remove a form from auto-assign list.
   */
  public async removeAutoAssignForm(
    companyId: string,
    formTemplateId: string,
    updatedBy: string
  ): Promise<void> {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return;

    const updatedForms = settings.autoAssignOnSignup.filter(id => id !== formTemplateId);

    await this.updateCompanySettings(
      companyId,
      { autoAssignOnSignup: updatedForms },
      updatedBy
    );
  }

  /**
   * Mark a form as optional (recommended but not required).
   */
  public async markFormAsOptional(
    companyId: string,
    formTemplateId: string,
    updatedBy: string
  ): Promise<void> {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return;

    const autoAssign = settings.autoAssignOnSignup.filter(id => id !== formTemplateId);
    const optional = new Set(settings.optionalForms);
    optional.add(formTemplateId);

    await this.updateCompanySettings(
      companyId,
      {
        autoAssignOnSignup: autoAssign,
        optionalForms: Array.from(optional)
      },
      updatedBy
    );
  }

  /**
   * Get all form requirements for a company (for UI display, from cache).
   */
  public async getFormRequirements(companyId: string = this.DEFAULT_COMPANY_ID): Promise<FormRequirementRule[]> {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return [];

    const FormTemplateService = require('./FormTemplateService').default;
    const templateService = FormTemplateService.getInstance();
    const allTemplates = await templateService.getAllFormTemplates();

    const requirements: FormRequirementRule[] = [];

    allTemplates.forEach((template: any, index: number) => {
      const isAutoAssigned = settings.autoAssignOnSignup.includes(template.id);
      const isOptional = settings.optionalForms.includes(template.id);

      requirements.push({
        formTemplateId: template.id,
        formName: template.name,
        category: template.category,
        isRequired: isAutoAssigned,
        isAutoAssigned: isAutoAssigned,
        displayOrder: isAutoAssigned ? index : index + 100
      });
    });

    return requirements.sort((a, b) => {
      if (a.isRequired && !b.isRequired) return -1;
      if (!a.isRequired && b.isRequired) return 1;
      return a.displayOrder - b.displayOrder;
    });
  }

  /**
   * Toggle appointment booking block.
   */
  public async setBookingBlock(
    companyId: string,
    shouldBlock: boolean,
    updatedBy: string
  ): Promise<void> {
    await this.updateCompanySettings(
      companyId,
      { blockBookingUntilComplete: shouldBlock },
      updatedBy
    );
  }

  /**
   * Get summary for admin dashboard (from cache).
   */
  public getSettingsSummary(companyId: string = this.DEFAULT_COMPANY_ID): {
    requiredFormsCount: number;
    optionalFormsCount: number;
    blockingEnabled: boolean;
    lastUpdated: string;
    updatedBy: string;
  } | null {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return null;

    return {
      requiredFormsCount: settings.autoAssignOnSignup.length,
      optionalFormsCount: settings.optionalForms.length,
      blockingEnabled: settings.blockBookingUntilComplete,
      lastUpdated: settings.updatedAt,
      updatedBy: settings.updatedBy
    };
  }

  /**
   * Reset to default settings.
   */
  public async resetToDefaults(companyId: string, updatedBy: string): Promise<void> {
    await this.updateCompanySettings(
      companyId,
      {
        autoAssignOnSignup: ['standard-intake', 'financial-responsibility', 'gad7', 'phq9'],
        blockBookingUntilComplete: true,
        optionalForms: ['covid-screening', 'release-information']
      },
      updatedBy
    );
  }

  /**
   * Export company settings as JSON (from cache).
   */
  public exportSettings(companyId: string): string {
    const settings = this.getCompanySettings(companyId);
    if (!settings) return '{}';

    const exportData = {
      exportDate: new Date().toISOString(),
      companySettings: settings
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import company settings from JSON.
   */
  public async importSettings(
    companyId: string,
    jsonData: string,
    importedBy: string
  ): Promise<boolean> {
    try {
      const data = JSON.parse(jsonData);

      if (data.companySettings) {
        await this.updateCompanySettings(
          companyId,
          {
            ...data.companySettings,
            companyId
          },
          importedBy
        );
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error importing settings:', error);
      return false;
    }
  }
}

export default CompanyFormSettingsService;
